/**
 * TableChart — Table/grid chart component.
 *
 * Reads from unified ChartData:
 *   columns: [{name, type}, ...]
 *   rows: [[val, val, ...], ...]
 *
 * Renders using Fluent UI DataGrid with sorting and resizable columns.
 *
 * Performance: cell values are pre-formatted in a useMemo pass so that
 * renderCell does zero computation — it only reads a cached display string.
 */
import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import {
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableCellLayout,
  TableColumnDefinition,
  createTableColumn,
} from '@fluentui/react-components';
import { formatValue, formatDate, isEpochTimestamp } from '../../../utils/formatters';
import type { ChartData } from '../echarts/optionBuilders';

export interface TableChartProps {
  data: ChartData;
}

interface TableRow {
  [key: string]: unknown;
}

/** Display-string key prefix for pre-formatted values */
const D = '__d_';

/** Approximate height of the Fluent UI DataGridHeader row (px) */
const HEADER_HEIGHT = 36;

/**
 * Format a single cell value based on column type.
 * Called once per cell during useMemo — never in renderCell.
 */
function formatCellValue(
  value: unknown,
  colType: string | undefined,
): string {
  if (colType === 'date' || colType === 'datetime' || colType === 'timestamp') {
    const fmt = colType === 'date' ? 'date' : 'datetime';
    return formatDate(value as string | number | Date | null | undefined, fmt);
  }
  if (typeof value === 'number' && isEpochTimestamp(value)) {
    return formatDate(value, 'datetime');
  }
  return formatValue(value);
}

const TableChartInner: React.FC<TableChartProps> = ({ data }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState<number | null>(null);

  const measure = useCallback(() => {
    if (containerRef.current) {
      const h = containerRef.current.clientHeight;
      setBodyHeight(Math.max(0, h - HEADER_HEIGHT));
    }
  }, []);

  useEffect(() => {
    measure();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      // Skip when hidden (e.g. ghost mode during drag/resize) —
      // visibility: hidden makes offsetParent null.
      if (el.offsetParent != null) {
        measure();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const { columns, items } = useMemo(() => {
    if (!data.columns.length || !data.rows.length) {
      return { columns: [], items: [] };
    }

    // Pre-format all cell values once. Each row stores both the raw value
    // (for sorting) and a display string (for renderCell).
    const rowItems: TableRow[] = data.rows.map((row, index) => {
      const rowObj: TableRow = { id: index };
      data.columns.forEach((col, colIndex) => {
        const raw = row[colIndex];
        rowObj[col.name] = raw;
        rowObj[`${D}${col.name}`] = formatCellValue(raw, col.type);
      });
      return rowObj;
    });

    const columnDefs: TableColumnDefinition<TableRow>[] = data.columns.map((col) => {
      const displayKey = `${D}${col.name}`;
      return createTableColumn<TableRow>({
        columnId: col.name,
        compare: (a, b) => {
          const aVal = a[col.name];
          const bVal = b[col.name];

          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return aVal - bVal;
          }

          if (col.type === 'date') {
            const aDate = new Date(aVal as string | number).getTime();
            const bDate = new Date(bVal as string | number).getTime();
            if (!isNaN(aDate) && !isNaN(bDate)) return aDate - bDate;
          }

          return String(aVal ?? '').localeCompare(String(bVal ?? ''));
        },
        renderHeaderCell: () => col.name,
        // Read pre-formatted string — zero computation at render time
        renderCell: (item) => (
          <TableCellLayout>{item[displayKey] as string}</TableCellLayout>
        ),
      });
    });

    return { columns: columnDefs, items: rowItems };
  }, [data]);

  if (columns.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        No table data available
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', overflow: 'hidden' }}
    >
      <DataGrid
        items={items}
        columns={columns}
        sortable
        resizableColumns
        style={{ minWidth: 0 }}
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<TableRow>
          style={
            bodyHeight != null
              ? { maxHeight: `${bodyHeight}px`, overflowY: 'auto', overflowX: 'auto' }
              : undefined
          }
        >
          {({ item, rowId }) => (
            <DataGridRow<TableRow> key={rowId}>
              {({ renderCell }) => (
                <DataGridCell>{renderCell(item)}</DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </div>
  );
};

const TableChart = React.memo(TableChartInner);
export default TableChart;
